/**
 * MobileBottomNav Component (Client Island)
 *
 * Diamond Architecture: Client Component for mobile navigation.
 * Fixed bottom tab bar with 4 tabs: Explore, Saved, Messages, Profile.
 *
 * Hidden on desktop (md:hidden).
 */
import { useUnreadConversationsCount } from '@/shared/hooks/useUnreadConversationsCount';
import { Link, usePathname } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { formatBadgeCount } from '@letsgethome/lib/wasm';
import { stripLocale } from '@letsgethome/lib/weburl';
import { routes, type Route } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { Heart, MessageSquare, Search, User } from 'lucide-react';

type NavItem = {
  href: Route;
  icon: React.ElementType;
  labelKey: 'explore' | 'saved' | 'messages' | 'profile';
  matchPaths?: string[];
};

const NAV_ITEMS: NavItem[] = [
  {
    href: routes.home(),
    icon: Search,
    labelKey: 'explore',
    // Derived from the routes builders — the bare '/listings' prefix literal
    // could silently detach from the SSOT on a rename (R5 PR-5).
    matchPaths: ['/', routes.listings.rent(), routes.listings.buy()],
  },
  {
    href: routes.account.saved(),
    icon: Heart,
    labelKey: 'saved',
    matchPaths: [routes.account.saved()],
  },
  {
    href: routes.account.conversations.index(),
    icon: MessageSquare,
    labelKey: 'messages',
    matchPaths: [routes.account.conversations.index()],
  },
  // The Profile tab is a PLAIN LINK again (PR-W1): /profile is a registered
  // web route serving the Rails-parity three-state page (signed-out shell for
  // anonymous visitors), so the SSR anchor resolves — the routes-closure gate
  // in @letsgethome/routes proves it. (While the route did not exist, the tab
  // was an auth-gated button — the Ahrefs health-80 404-href incident.)
  {
    href: routes.account.profile(),
    icon: User,
    labelKey: 'profile',
    matchPaths: [routes.account.profile()],
  },
];

export function MobileBottomNav() {
  const t = useTranslations('nav');
  const tA11y = useTranslations('common');
  const pathname = usePathname();
  const unreadConversationsCount = useUnreadConversationsCount();

  // The Messages tab badge — the Rust SSOT `formatBadgeCount` (0 → undefined
  // → no badge, >99 → "99+"), fed by its own `/api/conversations/unread-count`
  // query (R5: decoupled from auth hydration).
  const messagesBadge = formatBadgeCount(unreadConversationsCount);

  // Remove locale prefix for matching (the @letsgethome/lib/weburl SSOT —
  // boundary-aware, so `/ending` is not mistaken for an `/en` prefix).
  const pathWithoutLocale = stripLocale(pathname);

  const matchesPaths = (paths: string[]) =>
    paths.some((path) => {
      if (path === '/') return pathWithoutLocale === '/';
      return pathWithoutLocale.startsWith(path);
    });

  const isActive = (item: NavItem) => {
    if (item.matchPaths) return matchesPaths(item.matchPaths);
    return pathWithoutLocale === item.href;
  };

  // The layer is `@letsgethome/ui/z-layers`' claim, not this file's:
  // this nav and a page's `StickyActionBar` are the two bands that can occupy
  // the bottom edge at once, and which one the reader meets is decided by two
  // integers stated there — never by DOM order, which is all the listing
  // page's contact bar ever had (LET-120). `no-raw-z-layer` bans a
  // hand-picked `z-*` here.
  //
  // The safe-area padding comes from the same seam because the class this file
  // used to carry, `pb-safe`, is not a Tailwind utility and is defined in no
  // `@utility` block, no config and no plugin: it emitted NOTHING for the
  // whole life of this component. `Z_LAYERS.bottomNav.safeArea` is an arbitrary
  // utility, so Tailwind mints it from the literal and it cannot be undefined.
  // (It still resolves to 0px until the document's viewport meta carries
  // `viewport-fit=cover` — see `z-layers`, where the viewport meta and the
  // insets are now welded to one another.)
  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 border-t border-border bg-background md:hidden',
        Z_LAYERS.bottomNav.safeArea,
        Z_LAYERS.bottomNav.className
      )}
      aria-label={tA11y('a11y.mobileNavigation')}
    >
      <div className="flex w-full">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);

          return (
            <Link
              key={item.href}
              href={item.href}
              prefetch="intent"
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-colors',
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <span className="relative">
                <Icon className={cn('h-5 w-5', active && 'fill-current')} />
                {item.labelKey === 'messages' && messagesBadge !== undefined && (
                  <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-white">
                    {plaintextString(messagesBadge)}
                  </span>
                )}
              </span>
              <span className="text-[11px] leading-4">{t(item.labelKey)}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
